import React, { forwardRef, useEffect, useRef } from "react";
import { Dot } from "@components";
import { humanReadableDuration } from '@helpers'
import { ArrowDownCircleIcon, ArrowUpCircleIcon, CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid"
import PropTypes from "prop-types";

const getIcon = (status, attendance) => {
  if (attendance === "absent") return <XCircleIcon className={`text-content2 h-12`} />
  if (attendance === "attended") return <CheckCircleIcon className={`text-content2 h-12`} />
  if (status === "live") return <CheckCircleIcon className={`text-content2 h-12`} />
  if (status === "expired") return <ArrowDownCircleIcon className={`text-content2 h-12`} />
  if (status === "upcoming") return <ArrowUpCircleIcon className={`text-content2 h-12`} />
}

const getStatusColor = (status, attendanceStatus) => {
  if (attendanceStatus === "attended") return "attended" // bg-attended & text-attended
  if (attendanceStatus === "absent") return "absent" // bg-absent & text-absent
  if (status === "live") return "blue-400" // bg-blue-400 & text-blue-400
  if (status === "expired") return "purple-400" // bg-purple-400 & text-purple-400
  if (status === "upcoming") return "booking" // bg-booking & text-booking
}

const Meeting = forwardRef(({ 
  attendanceStatus, 
  handleClick, 
  guardian, 
  icon, 
  isLastItem, 
  lesson, 
  status, 
  student, 
  subject, 
  startDate, 
  endDate 
}, ref) => {
  const statusColor = getStatusColor(status, attendanceStatus)
  const timeTillStart = humanReadableDuration(startDate)
  
  const getStatus = (status, attendanceStatus) => {
    if (attendanceStatus === "attended") return "Attended"
    if (attendanceStatus === "absent") return "Absent"
    if (status === "live") return "Live"
    if (status === "expired") return "Expired"
    if (status === "upcoming") return "Upcoming"
  }

  return (
    <li ref={ref}>
      <div className="relative z-20 pb-6 pl-1">
        {!isLastItem && (
          <span className="absolute left-7 top-4 -ml-px h-full w-0.5 bg-content3" aria-hidden="true"></span>
        )}
        
        <div className="relative flex items-center space-x-3">
          <span className={`flex size-12 items-center justify-center rounded-full bg-${statusColor} ring-4 ring-content3`}>
            <div>{icon}</div>
          </span>
    
          <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
            <div>
              <p className="text-sm font-semibold text-blue-400 cursor-pointer" onClick={(slotId) => handleClick(slotId)}>{student}</p>
              <p className="text-sm text-content2-foreground">{guardian}</p>

              {subject && (
                <p className="text-sm text-content2-foreground">{subject} (<span className="font-semibold">{lesson}</span>)</p>
              )}

              {!subject && (
                <p className="text-sm text-content2-foreground">{lesson}</p>
              )}
            </div>

            <div className="text-sm text-right text-content2-foreground whitespace-nowrap">
              <div className="flex items-center justify-end space-x-1">
                {status === "live" && <Dot color="live" pulse />}
                <p className={`font-semibold mb-0.5 text-${statusColor}`}>{getStatus(status, attendanceStatus)}</p>
              </div>
              
              <p className="font-semibold">{startDate.format("H:mm")} - {endDate.format("H:mm")}</p>

              {status === "live" && <p>Now</p>}

              {status !== "live" && (
                <p>{timeTillStart} {status === "expired" ? "ago" : ""}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </li>
  )
})

const UpcomingMeetings = ({ currentAppointment = null, handleClick, items }) => {
  const currentAppointmentRef = useRef(null);

  useEffect(() => {
    if (currentAppointmentRef.current) {
      currentAppointmentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [currentAppointment]);

  return (
    <ul className="-mb-8">
      {items.map((item, index) => {
        const isActive = currentAppointment && currentAppointment.id === item.id;

        return (
          <Meeting
            ref={isActive ? currentAppointmentRef : null} // Only set ref on the active item
            handleClick={() => handleClick(item.id)}
            attendanceStatus={item.attendance_status}
            key={item.id}
            id={item.id}
            icon={getIcon(item.status, item.attendance_status)}
            isLastItem={index === items.length - 1}
            guardian={item.guardian}
            lesson={item.lesson} 
            status={item.status} 
            student={item.student} 
            startDate={item.starts_at}
            subject={item.subject}
            endDate={item.ends_at} 
          />
        )
      })}
    </ul>
  )
}

UpcomingMeetings.propTypes = {
  currentAppointment: PropTypes.object,
  handleClick: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      attendance_status: PropTypes.string,
      guardian: PropTypes.string.isRequired,
      lesson: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      student: PropTypes.string.isRequired,
      starts_at: PropTypes.object.isRequired,
      subject: PropTypes.string,
      ends_at: PropTypes.object.isRequired,
    })
  ).isRequired,
}

export default UpcomingMeetings